// RU-ANLFIX: display helpers for the Analytics strategy table (pure, tested in stratTable.test.js).

export const PF_MIN_TRADES = 10;

// AF-2 / AF-10: a profit factor is a ratio or null. Null with trades = no losing trade; under PF_MIN_TRADES counted
// trades the ratio is only in the title. Never "nullx". `ratio` is the number shown (null when the cell reads "—").
export function pfDisplay(row) {
  const pf = row ? row.profit_factor : null;
  const n = row && typeof row.total_trades === "number" ? row.total_trades : 0;
  const isNum = typeof pf === "number" && Number.isFinite(pf);
  if (!isNum) {
    return { text: n > 0 ? "— (no losses)" : "—", tone: "none", ratio: null,
             title: n > 0 ? "No losing trade in the period: no profit factor" : null };
  }
  if (n < PF_MIN_TRADES) {
    return { text: "— (few trades)", tone: "none", ratio: null,
             title: `PF ${pf.toFixed(2)}x from ${n} trade${n === 1 ? "" : "s"} — too few to be meaningful` };
  }
  return { text: `${pf.toFixed(2)}x`, tone: pf >= 1 ? "good" : "bad", ratio: pf, title: null };
}

// AF-11: order of the table rows. key "pnl" (default: API order when dir is null) or "pf"; dir "desc" | "asc".
// PF: rows showing a ratio first by ratio, then every "—" row in API order (both directions). The unattributed
// row stays last; ties keep API order.
export function sortStrats(rows, key, dir) {
  const list = (rows || []).map((r, i) => ({ r, i }));
  const tail = list.filter((x) => x.r.unattributed);
  const body = list.filter((x) => !x.r.unattributed);
  const sign = dir === "asc" ? 1 : -1;
  if (key === "pf") {
    body.sort((a, b) => {
      const pa = pfDisplay(a.r).ratio;
      const pb = pfDisplay(b.r).ratio;
      if (pa == null || pb == null) return (pa == null) - (pb == null) || a.i - b.i;
      return sign * (pa - pb) || a.i - b.i;
    });
  } else if (key === "pnl" && dir) {
    body.sort((a, b) => sign * ((a.r.total_pnl || 0) - (b.r.total_pnl || 0)) || a.i - b.i);
  }
  return [...body, ...tail].map((x) => x.r);
}

// AF-12: closed trades booked at exactly ₹0.
export function zeroNote(row) {
  const n = row && row.zero_pnl_trades;
  if (!(n > 0)) return null;
  return { text: `${n} at ₹0`,
           title: `${n} closed trade${n === 1 ? "" : "s"} booked at ₹0 P&L (mostly closes without a quote at the entry `
             + "price): counted as non-wins in Win%, no effect on the profit factor" };
}

const symName = (s) => (s == null || s === "" ? "UNKNOWN" : String(s));
const norm = (s) => (typeof s === "string" ? s.trim().toUpperCase() : "");

// AF-4: the symbol(s) the row actually traded, and a "configured: X" hint when that differs from the DSL symbol.
export function symbolDisplay(row) {
  const traded = Array.isArray(row && row.traded_symbols) ? row.traded_symbols : null;
  if (!traded || traded.length === 0) {
    const s = row && row.symbol ? String(row.symbol) : "—";
    return { text: s, title: s, configured: null };
  }
  const names = traded.map((t) => symName(t.symbol));
  let text;
  if (names.length === 1) text = names[0];
  else if (names.length === 2) text = `${names[0]}, ${names[1]}`;
  else text = `${names[0]} +${names.length - 1}`;
  const title = traded.map((t) => `${symName(t.symbol)} (${t.trades} trade${t.trades === 1 ? "" : "s"})`).join(", ");
  const conf = norm(row.symbol);
  const same = names.length === 1 && norm(names[0]) === conf;
  return { text, title, configured: !row.unattributed && conf && !same ? String(row.symbol).trim() : null };
}

// AF-7: "duplicate ×N" badge and its tooltip naming the others.
export function duplicateBadge(row) {
  const n = row && row.duplicate_count;
  if (!(n >= 2)) return null;
  const label = { name: "same name", rules: "same symbol, timeframe and rules", stats: "same symbol and results" };
  const basis = (row.duplicate_basis || []).map((b) => label[b] || b);
  if (row.identical_stats) basis.push("identical trade stats");
  const others = (row.duplicate_of || []).map((o) => o.name || o.id);
  const more = n - 1 - others.length;
  const list = others.join(", ") + (more > 0 ? ` +${more} more` : "");
  return { text: `duplicate ×${n}`, title: `Duplicates${basis.length ? ` — ${basis.join(" · ")}` : ""}: ${list}` };
}
